import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDataEntry, useRefreshCounts } from "../hooks/useRecords";
import { apiSend } from "../lib/api";
import type { OrderRecord } from "../lib/types";

type Row = {
  rowid: string;
  date: string;
  name: string;
  rollTypes: string;
  amount: string;
  twin: string;
  option: string;
  status: string;
  notes: string;
  fading?: boolean;
};

function toRow(r: OrderRecord): Row {
  return {
    rowid: String(r.rowid),
    date: r["-date"] || "",
    name: r["full-name"] || "",
    rollTypes: r["roll-types"] || "",
    amount: String(r["amount-of-rolls"] ?? ""),
    twin: r["Twin Check Stickers"] || "",
    option: r.return_option_normalized || "",
    status: (r["return-status"] as string) || "",
    notes: r.notes || "",
  };
}

const RETURN_OPTIONS = [
  ["", "Select..."],
  ["no-negs", "No Negs"],
  ["pickup-cut", "Pickup Cut"],
  ["uncut", "Uncut"],
  ["mail-back", "Mail Back"],
];
const RETURN_STATUSES = [
  ["", "Select..."],
  ["cut", "Cut"],
  ["prepped", "Prepped"],
  ["ready to mail", "Ready to Mail"],
  ["tossed", "Tossed"],
  ["picked-up", "Picked Up"],
  ["mailed", "Mailed"],
];

export function DataEntryTab({ active }: { active: boolean }) {
  const qc = useQueryClient();
  const refreshCounts = useRefreshCounts();
  const [dateFilter, setDateFilter] = useState("");
  const { data } = useDataEntry(dateFilter, active);
  const [rows, setRows] = useState<Row[]>([]);

  useEffect(() => {
    if (data?.records) setRows(data.records.map(toRow));
  }, [data]);

  // Manual entry form
  const [m, setM] = useState({
    date: "",
    name: "",
    rollTypes: "",
    amount: "",
    twin: "",
    option: "",
    status: "",
    notes: "",
  });

  async function saveManual() {
    if (!m.name) {
      alert("Customer name is required");
      return;
    }
    const payload: Record<string, string> = {
      "-date": m.date || new Date().toLocaleDateString(),
      "date-sortable": m.date || new Date().toISOString().split("T")[0],
      "full-name": m.name,
      "roll-types": m.rollTypes,
      "amount-of-rolls": m.amount,
      "Twin Check Stickers": m.twin,
      "return-option": m.option,
      "return-status": m.status,
      notes: m.notes,
    };
    try {
      await apiSend("create-record", "POST", payload);
      setM({ date: "", name: "", rollTypes: "", amount: "", twin: "", option: "", status: "", notes: "" });
      qc.invalidateQueries({ queryKey: ["records", "data-entry"] });
      refreshCounts();
    } catch (err) {
      console.error("Error creating record:", err);
      alert("Failed to create record");
    }
  }

  async function updateRow(row: Row, field: string, value: string, next: Partial<Row>) {
    setRows((rs) => rs.map((r) => (r.rowid === row.rowid ? { ...r, ...next } : r)));
    try {
      await apiSend("update-record", "POST", { id: row.rowid, field, value });
      refreshCounts();
      // Row leaves the incomplete list once twin-check + return-option are both set.
      const merged = { ...row, ...next };
      if (merged.twin && merged.option) {
        setRows((rs) => rs.map((r) => (r.rowid === row.rowid ? { ...r, fading: true } : r)));
        setTimeout(() => setRows((rs) => rs.filter((r) => r.rowid !== row.rowid)), 500);
      }
    } catch (err) {
      console.error("Error updating field:", err);
      alert("Failed to update field");
    }
  }

  async function deleteRecord(id: string) {
    if (!confirm("Are you sure you want to delete this record?")) return;
    try {
      await apiSend("delete-record", "POST", { id });
      setRows((rs) => rs.filter((r) => r.rowid !== id));
      refreshCounts();
    } catch (err) {
      console.error("Error deleting record:", err);
      alert("Failed to delete record");
    }
  }

  return (
    <div className="tab-content" style={{ display: active ? undefined : "none" }}>
      <div className="data-entry-controls">
        <input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} placeholder="Filter by date" />
        <button onClick={() => setDateFilter("")}>Clear Filter</button>
      </div>

      <div className="manual-entry-section">
        <h3>Manual Entry</h3>
        <div className="table-container">
          <table id="manualEntryTable">
            <thead>
              <tr>
                <th>Date</th>
                <th>Customer Name</th>
                <th>Roll Types</th>
                <th>Amount</th>
                <th>Twin Check</th>
                <th>Return Option</th>
                <th>Status</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr id="manualEntryRow">
                <td><input type="date" className="manual-field" value={m.date} onChange={(e) => setM({ ...m, date: e.target.value })} /></td>
                <td><input type="text" className="manual-field" placeholder="Customer name..." value={m.name} onChange={(e) => setM({ ...m, name: e.target.value })} /></td>
                <td><input type="text" className="manual-field" placeholder="Roll types..." value={m.rollTypes} onChange={(e) => setM({ ...m, rollTypes: e.target.value })} /></td>
                <td><input type="number" className="manual-field" min={0} placeholder="0" value={m.amount} onChange={(e) => setM({ ...m, amount: e.target.value })} /></td>
                <td><input type="text" className="manual-field" placeholder="Twin check..." value={m.twin} onChange={(e) => setM({ ...m, twin: e.target.value })} /></td>
                <td>
                  <select className="manual-field" value={m.option} onChange={(e) => setM({ ...m, option: e.target.value })}>
                    {RETURN_OPTIONS.map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <select className="manual-field" value={m.status} onChange={(e) => setM({ ...m, status: e.target.value })}>
                    <option value="">Select...</option>
                    <option value="cut">Cut</option>
                    <option value="prepped">Prepped</option>
                    <option value="ready to mail">Ready to Mail</option>
                    <option value="tossed">Tossed</option>
                    <option value="picked-up">Picked Up</option>
                    <option value="mailed">Mailed</option>
                  </select>
                </td>
                <td><input type="text" className="manual-field" placeholder="Notes..." value={m.notes} onChange={(e) => setM({ ...m, notes: e.target.value })} /></td>
                <td><button onClick={saveManual} className="save-button">Save</button></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <h3>Incomplete Orders</h3>
      <div className="table-container">
        <table id="dataEntryTable">
          <thead>
            <tr>
              <th>Date</th>
              <th>Customer Name</th>
              <th>Roll Types</th>
              <th>Amount</th>
              <th>Twin Check</th>
              <th>Return Option</th>
              <th>Status</th>
              <th>Notes</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody id="dataEntryTableBody">
            {rows.map((row) => (
              <tr key={row.rowid} data-id={row.rowid} style={row.fading ? { transition: "opacity 0.5s", opacity: 0 } : undefined}>
                <td>{row.date}</td>
                <td>{row.name}</td>
                <td>{row.rollTypes}</td>
                <td>{row.amount}</td>
                <td>
                  <input
                    type="text"
                    className="editable-field twin-check-field"
                    placeholder="Enter twin check numbers..."
                    value={row.twin}
                    onChange={(e) => setRows((rs) => rs.map((r) => (r.rowid === row.rowid ? { ...r, twin: e.target.value } : r)))}
                    onBlur={(e) => updateRow(row, "Twin Check Stickers", e.target.value, { twin: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        updateRow(row, "Twin Check Stickers", e.currentTarget.value, { twin: e.currentTarget.value });
                      }
                    }}
                  />
                </td>
                <td>
                  <select
                    className="editable-field return-option-field"
                    value={row.option}
                    onChange={(e) => updateRow(row, "return-option", e.target.value, { option: e.target.value })}
                  >
                    {RETURN_OPTIONS.map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    className="editable-field return-status-field"
                    value={row.status}
                    onChange={(e) => updateRow(row, "return-status", e.target.value, { status: e.target.value })}
                  >
                    {RETURN_STATUSES.map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    type="text"
                    className="editable-field notes-field"
                    placeholder="Enter notes..."
                    value={row.notes}
                    onChange={(e) => setRows((rs) => rs.map((r) => (r.rowid === row.rowid ? { ...r, notes: e.target.value } : r)))}
                    onBlur={(e) => updateRow(row, "notes", e.target.value, { notes: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        updateRow(row, "notes", e.currentTarget.value, { notes: e.currentTarget.value });
                      }
                    }}
                  />
                </td>
                <td>
                  <button className="delete-button" onClick={() => deleteRecord(row.rowid)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
